import { createEmbeddedCheckout, type EmbeddedCheckout } from '../net/stripe-embed.js';
import { closeButton } from './hud.js';
import { uiText } from './text.js';
import './shop-modals.css';

/**
 * The payment window (ADR 0031): Stripe's embedded page, over the game and
 * the shop, so the player never leaves Shefira to pay. Closing it before
 * paying abandons the order, which Stripe expires on its own; the shop only
 * learns of a payment from the server, never from here.
 */

export interface CheckoutModalOptions {
  publishableKey: string;
  clientSecret: string;
  /** Stripe said the payment went through in the page. */
  onPaid: () => void;
  /** The window closed without a payment (cross, Escape, or a Stripe.js failure). */
  onAbandon: (reason: 'closed' | 'failed') => void;
}

export class CheckoutModal {
  private readonly root: HTMLElement;
  private readonly mountPoint: HTMLElement;
  private readonly status: HTMLElement;
  private checkout: EmbeddedCheckout | undefined;
  private settled = false;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    // Escape is Stripe's too while a field inside its iframe has the focus;
    // here it only ever reaches us from the game side of the page.
    event.stopPropagation();
    this.abandon('closed');
  };

  constructor(private readonly options: CheckoutModalOptions) {
    this.root = document.createElement('div');
    this.root.className = 'shop-modal';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', uiText('Paiement'));

    const frame = document.createElement('section');
    frame.className = 'shop-modal__window shop-modal__window--checkout';
    const title = document.createElement('h2');
    title.className = 'shop-modal__title';
    title.textContent = uiText('Paiement sécurisé');
    this.status = document.createElement('p');
    this.status.className = 'shop-modal__hint';
    this.status.textContent = uiText('Ouverture du paiement…');
    // Stripe draws on white: the page sits on its own light card inside the frame.
    this.mountPoint = document.createElement('div');
    this.mountPoint.className = 'shop-modal__stripe';
    frame.append(
      title,
      this.status,
      this.mountPoint,
      closeButton('Annuler le paiement (Échap)', () => {
        this.abandon('closed');
      }),
    );
    this.root.append(frame);
    document.body.append(this.root);
    // Capture: the shop and the game hear Escape after the window has closed.
    window.addEventListener('keydown', this.onKeyDown, true);
    void this.mount();
  }

  private async mount(): Promise<void> {
    try {
      const checkout = await createEmbeddedCheckout(
        this.options.publishableKey,
        this.options.clientSecret,
        () => {
          this.complete();
        },
      );
      if (this.settled) {
        checkout.destroy();
        return;
      }
      this.checkout = checkout;
      checkout.mount(this.mountPoint);
      this.status.textContent = uiText(
        'Carte bancaire, Apple Pay ou Google Pay, traités par Stripe.',
      );
    } catch (error) {
      console.error('[shefira] embedded checkout failed', error);
      this.abandon('failed');
    }
  }

  private complete(): void {
    if (this.settled) return;
    this.settled = true;
    this.teardown();
    this.options.onPaid();
  }

  private abandon(reason: 'closed' | 'failed'): void {
    if (this.settled) return;
    this.settled = true;
    this.teardown();
    this.options.onAbandon(reason);
  }

  private teardown(): void {
    window.removeEventListener('keydown', this.onKeyDown, true);
    this.checkout?.destroy();
    this.checkout = undefined;
    this.root.remove();
  }
}
